import {
  Component,
  computed,
  input,
  model,
  Signal,
  contentChild,
  TemplateRef,
  output
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSearch,
  lucideChevronUp,
  lucideChevronDown,
  lucideChevronsUpDown
} from '@ng-icons/lucide';
import { DataTableColumn, SortState } from './data-table.models';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [CommonModule, FormsModule, NgIconComponent],
  viewProviders: [
    provideIcons({ lucideSearch, lucideChevronUp, lucideChevronDown, lucideChevronsUpDown }),
  ],
  templateUrl: './data-table.component.html',
})
export class DataTableComponent<T> {
  // Inputs
  data = input.required<T[]>();
  columns = input.required<DataTableColumn<T>[]>();
  searchable = input<boolean>(false);
  selectable = input<boolean>(false);
  clickableRows = input<boolean>(false);

  rowClick = output<T>();

  // Custom template for cells
  cellTemplate = contentChild(TemplateRef);

  // Models (Two-way bindings)
  selectedItems = model<T[]>([]);

  // Internal state
  searchQuery = model<string>('');
  sortState = model<SortState<T> | null>(null);

  // Computed data
  processedData: Signal<T[]> = computed(() => {
    let result = [...this.data()];

    // Filtering
    const query = this.searchQuery()?.toLowerCase().trim();
    if (this.searchable() && query) {
      result = result.filter((item) => {
        return this.columns().some((col) => {
          const val = col.renderString ? col.renderString(item) : item[col.key as keyof T];
          return val?.toString().toLowerCase().includes(query);
        });
      });
    }

    // Sorting
    const sort = this.sortState();
    if (sort && sort.direction) {
      result.sort((a, b) => {
        const col = this.columns().find((c) => c.key === sort.column);
        let valA, valB;

        if (col?.sortValue) {
          valA = col.sortValue(a);
          valB = col.sortValue(b);
        } else if (col?.renderString) {
          valA = col.renderString(a);
          valB = col.renderString(b);
        } else {
          valA = a[sort.column as keyof T];
          valB = b[sort.column as keyof T];
        }

        if (valA === valB) return 0;
        
        const isAsc = sort.direction === 'asc';
        
        if (valA === null || valA === undefined || valA === '') return isAsc ? -1 : 1;
        if (valB === null || valB === undefined || valB === '') return isAsc ? 1 : -1;

        let type = col?.sortType || typeof valA;

        if (type === 'number') {
          const numA = Number(valA);
          const numB = Number(valB);
          return isAsc ? numA - numB : numB - numA;
        }

        if (type === 'date') {
          const dateA = new Date(valA).getTime();
          const dateB = new Date(valB).getTime();
          return isAsc ? dateA - dateB : dateB - dateA;
        }

        if (type === 'boolean') {
          return isAsc ? (valA === valB ? 0 : valA ? 1 : -1) : (valA === valB ? 0 : valA ? -1 : 1);
        }

        return isAsc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
      });
    }

    return result;
  });

  // Checkbox Selection Logic
  get isAllSelected(): boolean {
    const currentData = this.processedData();
    if (currentData.length === 0) return false;
    return currentData.every((item) => this.selectedItems().includes(item));
  }

  get isIndeterminate(): boolean {
    const currentData = this.processedData();
    const selectedCount = currentData.filter((item) =>
      this.selectedItems().includes(item)
    ).length;
    return selectedCount > 0 && selectedCount < currentData.length;
  }

  toggleAllSelection(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    const currentData = this.processedData();
    let currentSelected = [...this.selectedItems()];

    if (isChecked) {
      // Add all visible items to selection
      currentData.forEach((item) => {
        if (!currentSelected.includes(item)) {
          currentSelected.push(item);
        }
      });
    } else {
      // Remove all visible items from selection
      currentSelected = currentSelected.filter(
        (sel) => !currentData.includes(sel)
      );
    }
    this.selectedItems.set(currentSelected);
  }

  toggleSelection(item: T, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    let currentSelected = [...this.selectedItems()];

    if (isChecked) {
      currentSelected.push(item);
    } else {
      currentSelected = currentSelected.filter((sel) => sel !== item);
    }
    this.selectedItems.set(currentSelected);
  }

  isSelected(item: T): boolean {
    return this.selectedItems().includes(item);
  }

  onRowClick(row: T) {
    if (this.clickableRows()) {
      this.rowClick.emit(row);
    }
  }

  // Sorting Logic
  handleSort(column: DataTableColumn<T>) {
    if (!column.sortable) return;

    const currentSort = this.sortState();
    if (currentSort?.column === column.key) {
      if (currentSort.direction === 'asc') {
        this.sortState.set({ column: column.key, direction: 'desc' });
      } else if (currentSort.direction === 'desc') {
        this.sortState.set(null); // Clear sort
      }
    } else {
      this.sortState.set({ column: column.key, direction: 'asc' });
    }
  }

  getValue(row: T, col: DataTableColumn<T>): any {
    if (col.renderString) {
      return col.renderString(row);
    }
    return row[col.key as keyof T];
  }
}
